import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoadingGate } from "../../../../../../../hooks/useLoadingGate";

import { useEffect, useMemo, useRef, useState } from "react";
import AuthCard from "../../../../../../../components/auth/AuthCard";
import BottomNav from "../../../../../../../components/common/BottomNav";
import { ChevronDownIcon } from "lucide-react";
import {
    VIDEOS_BY_ITEM,
    CATEGORY_OPTIONS,
    TITLES,
    TITLE_TO_KEY,
} from "../../../data/videos";

// MSK에서 쓰던 패턴 그대로
const ensureArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

export default function PainExerciseGuideVideo() {
    const ready = useLoadingGate();
    const navigate = useNavigate();
    const [sp] = useSearchParams();

    // 드롭다운 상태
    const [open, setOpen] = useState(false);
    const [cat, setCat] = useState("");
    const selectRef = useRef(null);

    useEffect(() => {
        const qItem = sp.get("item") || "abdomen_lumbar";

        if (VIDEOS_BY_ITEM[qItem]) {
            setCat(qItem);
            return;
        }

        const mapped = TITLE_TO_KEY[qItem];
        if (mapped) {
            setCat(mapped);
            return;
        }

        setCat(qItem);
    }, [sp]);

    // 바깥 클릭 시 드롭다운 닫기
    useEffect(() => {
        const onDoc = (e) => {
            if (!selectRef.current) return;
            if (!selectRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // 현재 카테고리 라벨
    const catLabel = useMemo(
        () => TITLES[cat] ?? "",
        [cat],
    );

    // 현재 카테고리 영상 리스트
    const list = useMemo(
        () => VIDEOS_BY_ITEM[cat] ?? [],
        [cat],
    );

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 md:px-8 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    title="통증별 운동 가이드"
                    onClose={() => navigate(-1)}
                >
                    <div className="flex flex-col flex-1 pt-3 md:pt-4 pb-24 md:pb-28">
                        {/* 드롭다운 */}
                        <div className="relative" ref={selectRef}>
                            <button
                                type="button"
                                onClick={() => setOpen((v) => !v)}
                                className="w-full h-12 md:h-13 px-4 rounded-xl bg-gray-100 border border-gray-200 text-gray-900 flex items-center justify-between shadow-sm"
                                aria-haspopup="listbox"
                                aria-expanded={open}
                            >
                                <span className="font-medium">{catLabel}</span>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-gray-700 transition-transform ${open ? "rotate-180" : ""
                                        }`}
                                />
                            </button>

                            {open && (
                                <ul
                                    role="listbox"
                                    className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
                                >
                                    {CATEGORY_OPTIONS.map((o) => (
                                        <li key={o.key} role="option" aria-selected={cat === o.key}>
                                            <button
                                                className={`w-full text-left h-11 px-4 ${cat === o.key
                                                    ? "bg-[#E8F1FF] text-[#1F4FD3] font-semibold"
                                                    : "hover:bg-gray-100"
                                                    }`}
                                                onClick={() => {
                                                    setCat(o.key);
                                                    setOpen(false);
                                                }}
                                            >
                                                {o.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="my-3 md:my-4">
                            <div className="h-px bg-gray-100" />
                        </div>

                        {/* 비디오 그리드 (2열) */}
                        <ul className="grid grid-cols-2 gap-3 md:gap-4">
                            {list.map((v, idx) => (
                                <li key={`${v.id}-${idx}`}>
                                    <button
                                        type="button"
                                        className="w-full rounded-2xl bg-white transition"
                                        onClick={() => {
                                            // MSKVideoList와 동일한 패턴: 쿼리 + state 함께 전달
                                            const qs = new URLSearchParams({
                                                v: v.id,
                                                cat: "pain",
                                                item: cat,
                                                title: v.title,
                                            }).toString();

                                            navigate(
                                                `/workout/conditionguide/pain/video/detail?${qs}`,
                                                {
                                                    state: {
                                                        title: v.title,
                                                        poster: v.thumb,             // 썸네일 → 포스터
                                                        guide: ensureArray(v.guide), // 배열 형태로 정규화
                                                        videoUrl: v.videoUrl ?? "",
                                                    },
                                                },
                                            );
                                        }}
                                    >
                                        <div className="aspect-video w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                                            {v.thumb ? (
                                                <img
                                                    src={v.thumb}
                                                    alt={v.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : null}
                                        </div>
                                        <div className="px-3 py-2">
                                            <p className="text-sm md:text-base font-medium leading-tight line-clamp-2 text-center">
                                                {v.title}
                                            </p>
                                        </div>
                                    </button>
                                </li>
                            ))}
                            {list.length === 0 && (
                                <li className="col-span-2 text-center text-gray-500 py-10">
                                    준비된 영상이 없습니다.
                                </li>
                            )}
                        </ul>
                    </div>
                </AuthCard>
            )}

            <BottomNav className="h-16 md:h-[72px]" />
        </main>
    );
}
